import React, { useState, useEffect, useMemo } from "react";
import {
  BarChart,
  Bar,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  LabelList,
} from "recharts";
import { Card, Empty } from "antd";
import { ExportToExcel } from "../../../../utils";

export const ChartSeller = ({ data }) => {
  const [chartData, setChartData] = useState([]);

  useEffect(() => {
    const processData = (data) => {
      let salesBySeller = {};
      data?.forEach((item) => {
        const seller = item.seller;
        const saleAmount = item.saleAmount;

        if (!salesBySeller[seller]) {
          salesBySeller[seller] = {
            totalSales: 0,
            orderCount: 0,
            productCount: 0,
          };
        }

        salesBySeller[seller].totalSales += saleAmount;
        salesBySeller[seller].orderCount += 1;

        salesBySeller[seller].productCount += item.products?.reduce(
          (total, product) => total + product.quantity,
          0
        );
      });

      const newChartData = Object.keys(salesBySeller)?.map((seller) => {
        const sellerData = salesBySeller[seller];
        return {
          seller,
          totalSales: sellerData.totalSales,
          orderCount: sellerData.orderCount,
          productCount: sellerData.productCount,
          averageTicket: sellerData.productCount / sellerData.orderCount,
        };
      });

      const sortedChartData = newChartData?.sort((a, b) => b.totalSales - a.totalSales);
      return sortedChartData;
    };

    const processedData = processData(data);
    setChartData(processedData);
  }, [data]);

  const transformedData = useMemo(() => {

    let totalSalesExport = {};
    const exportDataArray = [];

    data?.forEach((order) => {

      if(!totalSalesExport[order.seller]){
        totalSalesExport[order.seller] = {};
      }
      
      if(!totalSalesExport[order.seller][order.department]){
        totalSalesExport[order.seller][order.department] = {};
      }

      if(!totalSalesExport[order.seller][order.department][order.province]){
        totalSalesExport[order.seller][order.department][order.province] = {};
      }

      if(!totalSalesExport[order.seller][order.department][order.province][order.district]){
        totalSalesExport[order.seller][order.department][order.province][order.district] = {};
      }

      if(!totalSalesExport[order.seller][order.department][order.province][order.district][order.salesclosingchannel]){
        totalSalesExport[order.seller][order.department][order.province][order.district][order.salesclosingchannel] = {};
      }
      order.products?.forEach((item) => {

        if (!totalSalesExport[order.seller][order.department][order.province][order.district][order.salesclosingchannel][item.category]) {
          totalSalesExport[order.seller][order.department][order.province][order.district][order.salesclosingchannel][item.category] = {};
        }

        if(!totalSalesExport[order.seller][order.department][order.province][order.district][order.salesclosingchannel][item.category][item.brand]){
          totalSalesExport[order.seller][order.department][order.province][order.district][order.salesclosingchannel][item.category][item.brand] = {
            totalSales: 0,
            totalItems: 0,
            totalOrders: 0,
            totalDelivered: 0,
          };    
        }

        totalSalesExport[order.seller][order.department][order.province][order.district][order.salesclosingchannel][item.category][item.brand].totalSales +=
          item.total * item.quantity;
        totalSalesExport[order.seller][order.department][order.province][order.district][order.salesclosingchannel][item.category][item.brand].totalItems += item.quantity;
        totalSalesExport[order.seller][order.department][order.province][order.district][order.salesclosingchannel][item.category][item.brand].totalOrders++;
        if(order.status === "Entregado"){
          totalSalesExport[order.seller][order.department][order.province][order.district][order.salesclosingchannel][item.category][item.brand].totalDelivered++;
        }
      });
    });
      // eslint-disable-next-line
    Object.keys(totalSalesExport)?.map((seller) => {
      // eslint-disable-next-line
      Object.keys(totalSalesExport[seller])?.map((department)=>{
        // eslint-disable-next-line
        Object.keys(totalSalesExport[seller][department])?.map((province)=>{
            // eslint-disable-next-line
          Object.keys(totalSalesExport[seller][department][province])?.map((district) => {
            // eslint-disable-next-line
            Object.keys(totalSalesExport[seller][department][province][district])?.map((closingChannel)=>{
              // eslint-disable-next-line
              Object.keys(totalSalesExport[seller][department][province][district][closingChannel])?.map((category)=>{
                // eslint-disable-next-line
                Object.keys(totalSalesExport[seller][department][province][district][closingChannel][category])?.map((brand) => {
                  const record = {
                    seller,
                    department,
                    province,
                    district,
                    closingChannel,
                    category,
                    brand,
                    ...totalSalesExport[seller][department][province][district][closingChannel][category][brand],
                    averageTicket : totalSalesExport[seller][department][province][district][closingChannel][category][brand].totalItems / totalSalesExport[seller][department][province][district][closingChannel][category][brand].totalOrders,
                  };
                  exportDataArray.push(record);
                });
              });
            });
          });
        });
      });
    });

    return exportDataArray;

  }, [data]);

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const { totalSales, productCount, orderCount, averageTicket } =
        payload[0].payload;

      return (
        <div
          style={{
            backgroundColor: "#fff",
            border: "1px solid #ccc",
            padding: "10px",
          }}
        >
          <b className="intro">{`${label}`}</b>
          <p className="intro">{`Ventas totales: S/ ${totalSales?.toFixed(
            2
          )}`}</p>
          <p className="desc">{`Cantidad de órdenes: ${orderCount}`}</p>
          <p className="desc">{`Cantidad de productos: ${productCount}`}</p>
          <p className="desc">{`Ticket promedio: ${averageTicket?.toFixed(
            2
          )}`}</p>
        </div>
      );
    }

    return null;
  };

  const renderCustomizedLabel = (props) => {
    const { x, y, value } = props;

    return (
      <text
        x={x + 37}
        y={y - 5}
        fill="#000"
        textAnchor="middle"
        dominantBaseline="start"
        style={{ fontSize: "14px" }}
      >
        {`S/ ${value?.toFixed(2)}`}
      </text>
    );
  };

  const exportData = transformedData?.map((item) => ({
    Vendedor:item.seller,
    Departamento:item.department,
    Provincia:item.province,
    Distrito:item.district,
    "Canal cierre": item.closingChannel,
    Categoria: item.category,
    Marca: item.brand,
    "Ventas totales": `$${item.totalSales.toFixed(2)}`,
    "Cantidad de productos": item.totalItems,
    "Cantidad de ordenes": item.totalOrders,
    "Ticket promedio": item.averageTicket.toFixed(2),
    "Ordenes entregadas": item.totalDelivered,
  }));

  const exportColumns = [
    { title: 'Vendedor', dataIndex: 'Vendedor', key: 'Vendedor' },
    { title: 'Departamento', dataIndex: 'Departamento', key: 'Departamento' },
    { title: 'Provincia', dataIndex: 'Provincia', key: 'Provincia' },
    { title: 'Distrito', dataIndex: 'Distrito', key: 'Distrito' },
    { title: 'Canal cierre', dataIndex: 'Canal cierre', key: 'Canal cierre' },
    { title: 'Categoría', dataIndex: 'Categoria', key: 'Categoria' },
    { title: 'Marca', dataIndex: 'Marca', key: 'Marca' },
    { title: 'Ventas totales', dataIndex: 'Ventas totales', key: 'Ventas totales' },
    { title: 'Cantidad de productos', dataIndex: 'Cantidad de productos', key: 'Cantidad de productos' },
    { title: 'Cantidad de ordenes', dataIndex: 'Cantidad de ordenes', key: 'Cantidad de ordenes' },
    { title: 'Ticket promedio', dataIndex: 'Ticket promedio', key: 'Ticket promedio' },
    { title: 'Ordenes entregadas', dataIndex: 'Ordenes entregadas', key: 'Ordenes entregadas' }
  ];

  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return chartData.length > 0 ? (
    <Card title="Vendedores" bordered={true}>
      <ResponsiveContainer width="100%" height={300}>
        <BarChart
          data={chartData}
          layout="horizontal"
          margin={{ top: 30, right: 30, left: 0, bottom: 5 }}
        >
          <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.5} />
          <XAxis
            dataKey="seller"
            tick={{ fontSize: "14px" }}
            tickFormatter={(value) => {
              const index = value.indexOf("@");
              return index !== -1 ? value.substring(0, index) : value;
            }}
          />
          <YAxis />
          <Tooltip content={<CustomTooltip />} />

          <Bar dataKey="totalSales" name="Total Ventas" fill="#2B767E">
            {windowWidth > 768 && (
              <LabelList dataKey="totalSales" content={renderCustomizedLabel} />
            )}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          marginTop: 10,
          marginRight: 20,
        }}
      >
        <ExportToExcel
          data={exportData}
          fileName="resumenVendedores"
          columns={exportColumns}
        />
      </div>
    </Card>
  ) : (
    <Card title="Vendedores" bordered={true}>
      <div
        style={{
          height: 300,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Empty description="No hay datos disponibles" />
      </div>
    </Card>
  );
};
